if (!customElements.get('featured-collection-tabs')) {
  class FeaturedCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabsList = this.querySelector('[role="tablist"]');
      this.tabsNav = this.querySelector('.featured-collection__tabs-nav');
      this.prevBtn = this.querySelector('.featured-collection__tabs-btn--prev');
      this.nextBtn = this.querySelector('.featured-collection__tabs-btn--next');

      // SC 2.1.1 / SC 2.4.7 — scroll the product slider to keep focused cards visible.
      // Attached here (not in global.js) to avoid impacting other SliderComponent instances.
      this.querySelectorAll('slider-component').forEach((sliderEl) => {
        const slider = sliderEl.querySelector('[id^="Slider-"]');
        if (!slider) return;
        sliderEl.addEventListener('focusin', (e) => {
          const slide = e.target.closest('.slider__slide');
          if (!slide) return;
          const sliderRect = slider.getBoundingClientRect();
          const slideRect = slide.getBoundingClientRect();
          // Slide is at least partially outside the right or left edge of the scroll container.
          if (slideRect.right > sliderRect.right || slideRect.left < sliderRect.left) {
            slide.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab));
        tab.addEventListener('keydown', (e) => this.handleKeydown(e));
      });

      if (this.prevBtn && this.nextBtn && this.tabsList) {
        this.prevBtn.addEventListener('click', () => {
          this.tabsList.scrollBy({ left: -this.tabsList.clientWidth * 0.6, behavior: 'smooth' });
        });
        this.nextBtn.addEventListener('click', () => {
          this.tabsList.scrollBy({ left: this.tabsList.clientWidth * 0.6, behavior: 'smooth' });
        });
        this.tabsList.addEventListener('scroll', () => this.updateTabScrollButtons(), { passive: true });
        this._ro = new ResizeObserver(() => this.updateTabScrollButtons());
        this._ro.observe(this.tabsList);
        this.updateTabScrollButtons();
      }
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
    }

    updateTabScrollButtons() {
      if (window.innerWidth >= 750 || !this.tabsList) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.tabsList;
      const hasOverflow = scrollWidth > clientWidth + 1;
      this.tabsNav.classList.toggle('has-overflow', hasOverflow);
      if (hasOverflow) {
        this.prevBtn.toggleAttribute('hidden', scrollLeft <= 1);
        this.nextBtn.toggleAttribute('hidden', scrollLeft + clientWidth >= scrollWidth - 1);
      } else {
        this.prevBtn.setAttribute('hidden', '');
        this.nextBtn.setAttribute('hidden', '');
      }
    }

    selectTab(selectedTab) {
      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        if (panel.id === selectedTab.getAttribute('aria-controls')) {
          panel.removeAttribute('hidden');
          // IntersectionObserver on scroll-trigger elements does not reliably fire
          // when the parent goes from display:none to display:block, leaving
          // scroll-trigger--offscreen (visibility:hidden, opacity:0.01) stuck.
          // Same pattern as Dawn's global.js accordion handler (line 108).
          panel.querySelectorAll('.scroll-trigger').forEach((el) =>
            el.classList.remove('scroll-trigger--offscreen'),
          );
          // Force slider reinitialization once the browser has laid out the panel.
          const sliders = panel.querySelectorAll('slider-component');
          setTimeout(() => {
            sliders.forEach((slider) => {
              if (slider.resetPages) slider.resetPages();
            });
            window.dispatchEvent(new Event('resize'));
          }, 0);
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }

    handleKeydown(e) {
      const index = this.tabs.indexOf(e.target);
      if (e.key === 'ArrowRight') {
        const next = this.tabs[(index + 1) % this.tabs.length];
        this.selectTab(next);
        next.focus();
      } else if (e.key === 'ArrowLeft') {
        const prev = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        this.selectTab(prev);
        prev.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.selectTab(this.tabs[0]);
        this.tabs[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        this.selectTab(this.tabs[this.tabs.length - 1]);
        this.tabs[this.tabs.length - 1].focus();
      }
    }
  }

  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
